import React, { PropsWithChildren } from 'react';
import { makeStyles } from '@material-ui/core';
import HomeIcon from '@material-ui/icons/Home';
import DescriptionIcon from '@material-ui/icons/Description';
import HelpIcon from '@material-ui/icons/Help';
import EditIcon from '@material-ui/icons/Edit';
import CodeIcon from '@material-ui/icons/Code';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import PersonIcon from '@material-ui/icons/Person';
import {
    Settings as SidebarSettings,
    UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import {
    Sidebar,
    sidebarConfig,
    SidebarGroup,
    SidebarItem,
    SidebarPage,
    SidebarScrollWrapper,
    useSidebarOpenState,
    Link,
} from '@backstage/core-components';
import { useApi, configApiRef } from "@backstage/core-plugin-api";
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { sidebarTranslationRef } from '../../translations/sidebar';
import MenuIcon from '@material-ui/icons/Menu';
import CaiseLogoNegative from './CaiseLogoNegative';
import CaiseSidebarDivider from './CaiseSidebarDivider';
import CaiseSidebarSpace from './CaiseSidebarSpace';

const useSidebarLogoStyles = makeStyles({
    root: {
        width: sidebarConfig.drawerWidthOpen,
        height: 3 * sidebarConfig.logoHeight,
        display: 'flex',
        flexFlow: 'row nowrap',
        alignItems: 'center',
        justifyContent: 'flex-start',
        margin: 0,
        padding: '0 0.5rem 0 0.5rem',
        gap: '1.0rem',
    },
    rootClosed: {
        width: sidebarConfig.drawerWidthClosed,
        height: 3 * sidebarConfig.logoHeight,
        display: 'flex',
        flexFlow: 'column nowrap',
        alignItems: 'stretch',
        justifyContent: 'space-evenly',
        margin: 0,
        padding: '0.25rem 0 0.25rem 0',
        gap: '0',
    },
    link: {
        flexGrow: 0,
        minHeight: 0,
        width: 'auto',
        height: '100%',
    },
});

const SidebarLogo = () => {
    const { isOpen } = useSidebarOpenState();
    const caiseLogo = <CaiseLogoNegative />;
    const classes = useSidebarLogoStyles();

    return (
        <div className={isOpen ? classes.root : classes.rootClosed}>
            <Link to="/" underline="none" className={classes.link} aria-label="Home">
                {caiseLogo}
            </Link>
        </div>
    );
};

export const Root = ({ children }: PropsWithChildren<{}>) => {
    const { t } = useTranslationRef(sidebarTranslationRef);
    const configApi = useApi(configApiRef);
    const keySuffix = t('sidebar.keySuffix');
    const icons = {
        help: HelpIcon,
        documentation: DescriptionIcon,
        account: PersonIcon,
    }

    const items = [];
    for(const cfg of configApi.getConfigArray('homeResources')) {
        const title = cfg.getString('title' + keySuffix);
        const url = cfg.getString('url');
        // @ts-ignore
        items.push({title, url, icon: (icons[cfg.getString('icon')] || icons['help'])});
    }
    return (
        <SidebarPage>
            <Sidebar sidebarOptions={{drawerWidthOpen: 288, drawerWidthClosed: 90}}>
                <SidebarLogo />
                <SidebarGroup label="Menu" icon={<MenuIcon />}>
                    {/* Global nav, not org-specific */}
                    <SidebarItem icon={HomeIcon} to="/" text={t('sidebar.home')} />
                    <SidebarItem icon={EditIcon} to="experiments" text={t('sidebar.experiments')} />
                    <SidebarItem icon={CodeIcon} to="development" text={t('sidebar.development')} />
                    <SidebarItem icon={PlayArrowIcon} to="services" text={t('sidebar.services')} />
                    {/* End global nav */}
                    <CaiseSidebarDivider />
                    <SidebarScrollWrapper>
                        {items.map(({title, url, icon}) => (
                            <SidebarItem icon={icon} to={url} target="_blank" text={title} />)
                        )}
                        {/* Items in this group will be scrollable if they run out of space */}
                    </SidebarScrollWrapper>
                    <CaiseSidebarDivider />
                </SidebarGroup>
                <CaiseSidebarSpace />
                <CaiseSidebarDivider />
                <SidebarGroup
                    label={t('sidebar.settings')}
                    icon={<UserSettingsSignInAvatar />}
                    to="/settings"
                >
                    <SidebarSettings />
                </SidebarGroup>
            </Sidebar>
            {children}
        </SidebarPage>
    )
};
